"use client";

import { CSSProperties } from "react";
import { BarChart3 } from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Line,
    LineChart,
    Pie,
    PieChart,
    XAxis,
    YAxis,
} from "recharts";

import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
    type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    ChartAppearance,
    ChartBlock as ChartBlockType,
    ChartPoint,
    ChartVariant,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import {
    chartPalette,
    chartPointColor,
    resolveChartAppearance,
} from "../../../lib/blockRegistry";
import RichText from "../text/richText";
import RichHtml from "./richHtml";
import { seedTitleHtml, stripHtml } from "../../../lib/titleHtml";
import { fieldTag } from "../../../lib/sectionRegistry";
import { useLomdaDir, useLomdaLocale } from "../../../hooks/useLomdaLocale";

/** A point counts once it has a label or a value — a chart of empty rows is not
 *  a chart, and half-typed rows shouldn't flicker in and out of the preview. */
const isFilled = (p: ChartPoint) => !!p.label.trim() || !!p.value;

const formatValue = (value: number, unit: string) =>
    unit ? `${value}${unit}` : `${value}`;

/** Axis/label text follows the lomda's own small-text size, so a customer
 *  restyling their lesson restyles the chart with it. */
const TICK_STYLE = {
    fill: "hsl(var(--lomda-muted-text))",
    fontSize: "var(--lomda-size-small)",
} as const;

/** Same, but pinned left-to-right: category names are Hebrew in a Hebrew
 *  lesson, while a value like "450₪" must not be reordered by the bidi rules. */
const VALUE_LABEL_STYLE = { ...TICK_STYLE, direction: "ltr" } as const;

function ChartCanvas({
    variant,
    points,
    appearance,
    palette,
    unit,
    rtl,
}: {
    variant: ChartVariant;
    points: ChartPoint[];
    appearance: ChartAppearance;
    palette: string[];
    unit: string;
    rtl: boolean;
}) {
    const locale = useLomdaLocale();
    const data = points.map((p, i) => ({
        ...p,
        label: p.label.trim(),
        fill: chartPointColor(p, i, palette),
    }));
    const config: ChartConfig = {
        value: { label: t("lomda-v3-editor.chart.value", locale) },
    };
    // A donut shows parts of ONE whole, so its numbers only mean anything as a
    // share of the total — it has no unit of its own.
    const total = data.reduce((sum, d) => sum + d.value, 0);
    const share = (value: number) =>
        total > 0 ? `${Math.round((value / total) * 100)}%` : "0%";
    const readValue = (value: number) =>
        variant === "donut" ? share(value) : formatValue(value, unit);
    const tooltip = (
        <ChartTooltip
            content={
                <ChartTooltipContent
                    // Bar/line name the hovered point from the category axis. A
                    // pie has no axis, so take the name off the slice itself —
                    // otherwise the donut's tooltip is a bare number.
                    labelFormatter={
                        variant === "donut"
                            ? (_label, items) =>
                                  String(items?.[0]?.payload?.label ?? "")
                            : undefined
                    }
                    // A measurement always reads left-to-right, even in Hebrew:
                    // without this "450₪" flips to "₪450".
                    formatter={(v) => <span dir="ltr">{readValue(Number(v))}</span>}
                />
            }
        />
    );
    const valueLabels = appearance.showValues && (
        <LabelList
            dataKey="value"
            // Sideways bars grow from the start edge, so the value belongs at
            // the bar's tip — which is the LEFT end once RTL reverses the axis.
            position={
                variant === "bar" && appearance.horizontal
                    ? rtl
                        ? "left"
                        : "right"
                    : "top"
            }
            formatter={(v: number) => readValue(v)}
            style={VALUE_LABEL_STYLE}
        />
    );

    if (variant === "donut") {
        return (
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-8">
                {/* Names first, diagram after — so the list reads on the start
                    side (left in English, right in Hebrew) and the donut on the
                    end side. Our own legend rather than recharts': it wraps
                    properly on mobile and follows the lomda typography. */}
                {/* The row splits 55/45 in favour of the names. A FIXED share,
                    not their natural width: with a narrow list ("a", "b") the
                    ring would otherwise slide toward the middle, so its place
                    would shift with every label change. */}
                <ul className="flex w-full min-w-0 flex-col gap-2 sm:w-[55%] sm:shrink-0">
                    {data.map((d) => (
                        <li key={d.id} className="flex items-center gap-2.5">
                            <span
                                className="size-3 shrink-0 rounded-full"
                                style={{ backgroundColor: d.fill }}
                            />
                            {/* No flex-1: the value sits right beside its name
                                rather than being pushed to the far edge. The
                                name still shrinks + truncates when space runs
                                out (flex items shrink by default). */}
                            <span className="min-w-0 truncate lomda-v3-text-p text-foreground">
                                {d.label}
                            </span>
                            {appearance.showValues && (
                                <span
                                    dir="ltr"
                                    className="shrink-0 lomda-v3-text-label text-[hsl(var(--lomda-muted-text))]"
                                >
                                    {share(d.value)}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
                {/* The donut fills the side it sits on instead of being a
                    fixed square pinned to the edge: it takes the width left
                    over from the names, capped so it stays a readable ring.
                    The box is only as tall as the ring itself — the radii are
                    raised to cancel the slack a default pie leaves inside its
                    square, so the block gets shorter without the ring shrinking. */}
                <div className="flex w-full justify-center sm:min-w-0 sm:flex-1">
                    <ChartContainer
                        config={config}
                        className="aspect-square w-full max-w-[290px]"
                    >
                        <PieChart>
                            {tooltip}
                            <Pie
                                data={data}
                                dataKey="value"
                                nameKey="label"
                                innerRadius="64%"
                                outerRadius="97%"
                                paddingAngle={2}
                                strokeWidth={0}
                                isAnimationActive={false}
                            >
                                {data.map((d) => (
                                    <Cell key={d.id} fill={d.fill} />
                                ))}
                            </Pie>
                        </PieChart>
                    </ChartContainer>
                </div>
            </div>
        );
    }

    if (variant === "line") {
        return (
            <ChartContainer
                config={config}
                className="h-[260px] w-full sm:h-[300px]"
            >
                <LineChart
                    data={data}
                    margin={{ top: 24, right: 12, bottom: 4, left: 12 }}
                >
                    <CartesianGrid
                        vertical={false}
                        stroke="hsl(var(--lomda-border))"
                    />
                    <XAxis
                        dataKey="label"
                        reversed={rtl}
                        tickLine={false}
                        axisLine={false}
                        tickMargin={10}
                        tick={TICK_STYLE}
                    />
                    <YAxis hide />
                    {tooltip}
                    <Line
                        dataKey="value"
                        type="monotone"
                        stroke={appearance.seriesColor}
                        strokeWidth={2}
                        isAnimationActive={false}
                        dot={{ fill: appearance.seriesColor, r: 4 }}
                        activeDot={{ r: 6 }}
                    >
                        {valueLabels}
                    </Line>
                </LineChart>
            </ChartContainer>
        );
    }

    // ── bar ──
    // recharts calls side-by-side bars "horizontal" layout and sideways bars
    // "vertical" — the naming is inverted from how an author reads it.
    const sideways = appearance.horizontal;
    return (
        <ChartContainer
            config={config}
            className={cn("w-full", sideways ? "" : "h-[260px] sm:h-[300px]")}
            style={
                sideways
                    ? { height: Math.max(200, data.length * 52) }
                    : undefined
            }
        >
            <BarChart
                data={data}
                layout={sideways ? "vertical" : "horizontal"}
                margin={{ top: 24, right: 16, bottom: 4, left: 16 }}
            >
                <CartesianGrid
                    horizontal={!sideways}
                    vertical={sideways}
                    stroke="hsl(var(--lomda-border))"
                />
                {sideways ? (
                    <>
                        <XAxis type="number" hide reversed={rtl} />
                        <YAxis
                            type="category"
                            dataKey="label"
                            orientation={rtl ? "right" : "left"}
                            tickLine={false}
                            axisLine={false}
                            width={96}
                            tickMargin={8}
                            tick={TICK_STYLE}
                        />
                    </>
                ) : (
                    <>
                        <XAxis
                            dataKey="label"
                            reversed={rtl}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={10}
                            tick={TICK_STYLE}
                        />
                        <YAxis hide />
                    </>
                )}
                {tooltip}
                <Bar dataKey="value" radius={6} isAnimationActive={false}>
                    {data.map((d) => (
                        <Cell key={d.id} fill={d.fill} />
                    ))}
                    {valueLabels}
                </Bar>
            </BarChart>
        </ChartContainer>
    );
}

export default function ChartBlock({ block }: { block: ChartBlockType }) {
    const titleTag = fieldTag("chart", "title");
    const editable = useBlockEditable();
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const rtl = useLomdaDir() === "rtl";

    const { variant, title, caption, unit, points } = block.data;
    const appearance = resolveChartAppearance(block.data.appearance, theme);
    // Sized to the data, and resolved here rather than inside the canvas: the
    // preview drops half-typed rows, and colouring by position AFTER that would
    // shift every colour the moment a row is left empty.
    const palette = chartPalette(theme, points.length);
    const colored = points.map((p, i) => ({
        ...p,
        color: chartPointColor(p, i, palette),
    }));

    const update = (patch: Partial<ChartBlockType["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });
    const hasData = colored.some(isFilled);

    const cardStyle: CSSProperties = {
        backgroundColor: appearance.cardBackground,
        borderColor: appearance.border,
        borderWidth: appearance.borderWidth,
        borderStyle: "solid",
    };

    if (editable) {
        return (
            <div
                className="lomda-v3-card-text flex flex-col gap-5 rounded-[20px] p-6"
                style={cardStyle}
            >
                <RichText
                    initialHtml={seedTitleHtml(title, titleTag)}
                    defaultTag={titleTag}
                    editable
                    useGlobalToolbar
                    blockId={block.id}
                    placeholder={t("lomda-v3-editor.chart.title-placeholder")}
                    onCommit={(html) => update({ title: html })}
                />

                {/* The same chart the learner sees, redrawing as the data is
                    edited in the topbar. Until there is data there is nothing
                    to draw, so point the author at where it's entered. */}
                {hasData ? (
                    <ChartCanvas
                        variant={variant}
                        points={colored}
                        appearance={appearance}
                        palette={palette}
                        unit={unit}
                        rtl={rtl}
                    />
                ) : (
                    <div className="lomda-v3-chrome flex items-center justify-center gap-2 rounded-xl border border-dashed py-8 text-sm text-muted-foreground">
                        <BarChart3 className="size-4" />
                        {t("lomda-v3-editor.chart.no-data")}
                    </div>
                )}

                <input
                    value={caption}
                    onChange={(e) => update({ caption: e.target.value })}
                    placeholder={t("lomda-v3-editor.chart.caption-placeholder")}
                    className="lomda-v3-page-text w-full rounded-lg border border-[hsl(var(--lomda-border))] bg-[hsl(var(--lomda-background))] px-3 py-2 lomda-v3-text-small outline-none placeholder:text-[hsl(var(--lomda-muted-text))] focus:border-[hsl(var(--lomda-border-active))]"
                />
            </div>
        );
    }

    const filled = colored.filter(isFilled);
    if (filled.length === 0) return null;

    return (
        <figure
            className="lomda-v3-card-text flex flex-col gap-5 rounded-[20px] p-6"
            style={cardStyle}
        >
            {stripHtml(title) && (
                <RichHtml html={seedTitleHtml(title, titleTag)} />
            )}
            <ChartCanvas
                variant={variant}
                points={filled}
                appearance={appearance}
                palette={palette}
                unit={unit}
                rtl={rtl}
            />
            {caption.trim() && (
                <figcaption className="lomda-v3-text-small text-[hsl(var(--lomda-muted-text))]">
                    {caption}
                </figcaption>
            )}
        </figure>
    );
}
